import React, { useRef, useState } from "react";
import {
  CautionGrey,
  ForgotPasswordIcon,
  MailGrey,
} from "@/components/bnp-icons";
import { Button } from "@/components/bnp-ui";
import { TextInput } from "@mantine/core";
import AuthWrapper from "@/components/layouts/AuthWrapper";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { AuthService } from "@/services";
import parseHttpError from "@/utils/parseHttpError";
import { useMutation } from "@tanstack/react-query";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import { toast } from "react-toastify";
import ReCAPTCHA from "react-google-recaptcha";

const defaultValues = {
  email: "",
  recaptchaToken: "",
};

const validationSchema = yup.object().shape({
  email: yup.string().email("Enter a valid email").required("Enter your email"),
  recaptchaToken: yup
    .string()
    .required("Please complete the reCAPTCHA verification"),
});

const ForgotPassword = () => {
  const router = useRouter();
  const recaptchaRef = useRef(null);
  const [recaptchaError, setRecaptchaError] = useState("");
  const RecaptchaComponent = ReCAPTCHA as unknown as React.FC<any>;

  const {
    getValues,
    setValue,
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
  });
  const handleRecaptchaChange = (value: any) => {
    setValue("recaptchaToken", value);
    setRecaptchaError("");
  };

  const forgotPasswordRequest = async () => {
    try {
      console.log(getValues());
      const response = await AuthService.forgotPassword(getValues());
      return response.data;
    } catch (error: any) {
      console.log(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const mutation: any = useMutation({
    mutationFn: forgotPasswordRequest,
    onSuccess: (res: any) => {
      // console.log(res?.data);
      const email = getValues().email;

      const encodedEmail = btoa(email);

      router.push(`/auth/verify?token=${encodedEmail}&a=reset`);
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to send verification code");
    },
  });

  const onSubmit = () => mutation.mutate();
  return (
    <AuthWrapper
      footerText={"Changed your mind?"}
      footerBtnText={"Go back"}
      footerLink={"/auth/sign-in"}
    >
      <div className="flex flex-col w-[100%]">
        <div className="flex flex-col items-center gap-4">
          <ForgotPasswordIcon />
          <div className="flex flex-col gap-1 items-center">
            <h2 className="text-main-900 font-semibold text-xl">
              Reset Password
            </h2>
            <p className="text-grey-7 text-sm font-normal">
              Enter your email to reset your password.
            </p>
          </div>
        </div>
        {/* <div className="flex items-center justify-between mt-6 w-[100%]">
          <div className="flex items-center justify-center bg-white  rounded-[10px] border border-soft-200 w-[100px] lg:w-[143px] h-[40px] ">
            <Apple />
          </div>
          <div className=" flex items-center justify-center bg-white  rounded-[10px] border border-soft-200  w-[100px] lg:w-[143px] h-[40px]">
            <Google />
          </div>

          <div className="flex items-center justify-center bg-white  rounded-[10px] border border-soft-200  w-[100px] lg:w-[143px] h-[40px]">
            <X />
          </div>
        </div>
        <div className=" flex items-center gap-2 text-xs text-grey-7 pt-6">
          <div className=" border-b w-[144px] lg:w-[208px] h-[1px] border-soft-200"></div>
          <div>OR</div>

          <div className=" border-b w-[144px] lg:w-[208px] h-[1px] border-soft-200"></div>
        </div> */}
        <div className=" w-[100%] mt-6">
          <form
            action={"submit"}
            className="flex flex-col gap-8"
            onSubmit={(e) => e.preventDefault()}
          >
            <Controller
              name="email"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextInput
                  type="email"
                  name="email"
                  label="Email Address"
                  value={value}
                  withAsterisk
                  placeholder="Email"
                  leftSection={<MailGrey />}
                  size="md"
                  radius={"md"}
                  className="w-[100%]  h-[52px]"
                  onChange={onChange}
                  onBlur={onBlur}
                  error={errors.email?.message}
                />
              )}
            />

            <div className="flex gap-1 items-center text-sm font-medium text-grey-7 text-center">
              <CautionGrey />
              <p> Enter the email you registered with</p>
            </div>

            <div className="flex flex-col items-center gap-2">
              <RecaptchaComponent
                ref={recaptchaRef}
                sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? ""}
                onChange={handleRecaptchaChange}
              />
              {(recaptchaError || errors.recaptchaToken) && (
                <span className="text-red-500 text-xs">
                  {recaptchaError || errors.recaptchaToken?.message}
                </span>
              )}
            </div>

            <Button
              className="bg-bnp-dark text-white text-sm font-extrabold w-[100%] h-[52px] rounded-lg mt-4 "
              isLoading={mutation.isPending}
              onClick={handleSubmit(onSubmit)}
            >
              Reset password
            </Button>
          </form>
        </div>
      </div>
    </AuthWrapper>
  );
};

export default ForgotPassword;
